<script lang="ts">
  import { useActions } from "#lib/internal/helpers"
  import type { BaseProps } from "#lib/internal/types"
  import { cn } from "#lib/utils"
  import {
    CheckboxFilled,
    Checkbox as CheckboxIcon,
    CheckboxSelectAll,
  } from "@tastyworks/icons"
  import {
    checkboxContainerVariants,
    checkboxIconVariants,
    checkboxLabelVariants,
    type CheckboxSize,
  } from "./variants"

  type Props = Omit<
    BaseProps<"input">,
    "checked" | "indeterminate" | "size" | "type"
  > & {
    label?: string
    checked?: boolean
    indeterminate?: boolean
    size?: CheckboxSize
    error?: boolean
  }

  let {
    class: className = undefined,
    // No $bindable fallback: form-backed call sites bind against fields that
    // start out undefined, and Svelte rejects bind: to undefined when the
    // bindable prop has one (props_invalid_value).
    checked = $bindable(),
    indeterminate = $bindable(),
    disabled = false,
    error = false,
    label = undefined,
    size = "medium",
    use = [],
    ...restProps
  }: Props = $props()
</script>

<label class={cn(checkboxContainerVariants(), className)}>
  <input
    type="checkbox"
    class="peer absolute inset-0 m-0 cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed"
    bind:checked
    bind:indeterminate
    {disabled}
    aria-invalid={error || undefined}
    use:useActions={use}
    {...restProps}
  />
  {#if indeterminate}
    <CheckboxSelectAll
      aria-hidden="true"
      class={cn(checkboxIconVariants({ size }))}
    />
  {:else if checked}
    <CheckboxFilled
      aria-hidden="true"
      class={cn(checkboxIconVariants({ size }))}
    />
  {:else}
    <!-- `error` reaches the empty box only: a filled box carries a value, so it
         is never the invalid one. -->
    <CheckboxIcon
      aria-hidden="true"
      class={cn(checkboxIconVariants({ size, error }))}
    />
  {/if}
  {#if label}
    <span class={cn(checkboxLabelVariants({ size }))}>{label}</span>
  {/if}
</label>
